import {
    ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostBinding,
    Input, Output, forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Cifra con unidad: temperatura, peso, medida.
 *
 * Antes la temperatura era un `<input type="text">` de 48 px pegado a un
 * `<select>` de 54, ambos sin borde dentro de una caja compartida y con el
 * chevron dibujado a mano con dos gradientes. Cabían dos caracteres y no se
 * entendía que °C fuera elegible.
 *
 * Decisiones:
 *
 * - **La cifra va a la derecha, en mono y con `tabular-nums`**, como toda cifra
 *   del sistema: `-18` y `-4` acaban alineados en la misma columna.
 * - **Pasos de más y de menos.** Una temperatura se ajusta de grado en grado
 *   mucho más a menudo que se teclea de cero. Con teclado, ↑ y ↓ hacen lo mismo.
 * - **Unidad como segmentado**, no como desplegable: son dos opciones y se ven
 *   las dos. Un desplegable de dos opciones cuesta dos clics para nada. Cuando
 *   hay una sola unidad, `units` se deja vacío y se pasa `suffix`.
 * - **Permite negativos** — es el caso normal en un contenedor refrigerado.
 */
@Component({
    standalone: true,
    selector: 'dxc-number-field',
    templateUrl: './dxc-number-field.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DxcNumberFieldComponent), multi: true }],
})
export class DxcNumberFieldComponent implements ControlValueAccessor {

    @Input() step = 1;
    @Input() min?: number;
    @Input() max?: number;
    @Input() placeholder = '';
    @Input() invalid = false;
    @Input() ariaLabel = '';
    @Input() fieldId = '';
    /** Muestra los botones de paso. Se apagan para un importe, que no se tantea. */
    @Input() steppers = true;
    /** Unidad fija, cuando no hay nada que elegir: `kg`, `m³`. */
    @Input() suffix = '';
    /** Unidades elegibles; con dos o tres se pinta un segmentado. */
    @Input() units: string[] = [];
    @Input() unit = '';
    @Output() unitChange = new EventEmitter<string>();

    @Output() valueChange = new EventEmitter<string>();

    @HostBinding('class.dxc-numfield') readonly hostClass = true;

    text = '';
    disabled = false;

    #onChange: (value: string) => void = () => { };
    #onTouched: () => void = () => { };

    constructor(private cdr: ChangeDetectorRef) { }

    // --- ControlValueAccessor ------------------------------------------------

    writeValue(value: string | number | null) {
        this.text = value === null || value === undefined ? '' : String(value);
        this.cdr.markForCheck();
    }
    registerOnChange(fn: (value: string) => void) { this.#onChange = fn; }
    registerOnTouched(fn: () => void) { this.#onTouched = fn; }
    setDisabledState(disabled: boolean) { this.disabled = disabled; this.cdr.markForCheck(); }

    // --- Entrada -------------------------------------------------------------

    /** Se acepta el signo y un separador decimal; el resto se descarta al teclear. */
    onType(text: string) {
        const cleaned = (text || '').replace(/[^\d.,-]/g, '').replace(',', '.');
        this.text = cleaned;
        this.#emit();
    }

    onBlur() {
        const parsed = this.#parse();
        if (parsed !== null) this.text = String(this.#clamp(parsed));
        this.#onTouched();
        this.#emit();
    }

    nudge(direction: 1 | -1) {
        if (this.disabled) return;
        const base = this.#parse() ?? 0;
        // Se redondea al múltiplo del paso para que 17.4 + 1 dé 18, no 18.4.
        const next = this.#clamp(Math.round((base + direction * this.step) / this.step) * this.step);
        this.text = String(Number(next.toFixed(4)));
        this.#emit();
    }

    onKeydown(event: KeyboardEvent) {
        if (event.key === 'ArrowUp') { this.nudge(1); event.preventDefault(); }
        if (event.key === 'ArrowDown') { this.nudge(-1); event.preventDefault(); }
    }

    setUnit(unit: string) {
        if (this.disabled || this.unit === unit) return;
        this.unit = unit;
        this.unitChange.emit(unit);
        this.cdr.markForCheck();
    }

    get atMin() { return this.min !== undefined && (this.#parse() ?? 0) <= this.min; }
    get atMax() { return this.max !== undefined && (this.#parse() ?? 0) >= this.max; }

    // --- Interno -------------------------------------------------------------

    #parse(): number | null {
        if (this.text === '' || this.text === '-') return null;
        const parsed = Number(this.text);
        return isNaN(parsed) ? null : parsed;
    }

    #clamp(value: number) {
        let next = value;
        if (this.min !== undefined) next = Math.max(this.min, next);
        if (this.max !== undefined) next = Math.min(this.max, next);
        return next;
    }

    #emit() {
        this.#onChange(this.text);
        this.valueChange.emit(this.text);
        this.cdr.markForCheck();
    }
}
